import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AddItemModal } from '../../components/AddItemModal';
import { ItemDetails } from '../../components/RecordFields';
import { SearchSelect } from '../review/reviewUi';
import { riskApi } from '../../utils/riskApi';
import { ControlPill, LevelPill } from './riskUi';
import { anomalyItems } from './RiskPanelCard';
import '../../styles/DocReview.css';
import '../../styles/Risk.css';

const RESULT_STYLE = {
  pass: { background: '#DDF3E4', borderColor: '#9FD3B4', label: 'Passed' },
  fail: { background: '#FBE0D5', borderColor: '#E9A88E', label: 'Failed' },
  not_run: { background: '#F3F0F8', borderColor: '#E4E0EE', label: 'Not run' },
  // Found through the relations, but no test filter carries its result — not "not run".
  unknown: { background: '#FFFFFF', borderColor: '#D4D0E0', label: 'Result not read — not in a test filter' },
};

/** A key that opens the item in Orcanos — the server resolves the URL from the item's type CODE
 *  (a key's prefix is only a display alias: TC is T_CASE), so no URL is ever built here. */
function KeyLink({ href, k }) {
  return href
    ? <a className="rsk-olink" href={href} target="_blank" rel="noopener noreferrer" title={`Open ${k} in Orcanos`}>{k}</a>
    : k;
}

/**
 * One risk (mockup screen 3): is it controlled? — risk → mitigation → verification → last run,
 * its scores before and after, what looks wrong about it, and its live Orcanos properties.
 */
export default function RiskDetailPage() {
  const { setupId, itemId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('trace');
  const [hist, setHist] = useState(null);

  // {done, total} while the "Open in Orcanos" links resolve — two Orcanos calls per mitigation
  // and test, so a risk with many relations takes a while. The page shows without them first.
  const [links, setLinks] = useState(null);

  // ＋ Add mitigation — the matrix's add / ✨ Generate with AI modal, pointed at this risk.
  // `mit` = what may be offered (types, learned default, link type, gates); null until read.
  const [mit, setMit] = useState(null);
  const [mitType, setMitType] = useState('');
  const [adding, setAdding] = useState(false);
  const [addedAny, setAddedAny] = useState(false);
  const [notice, setNotice] = useState(null);     // {kind: 'ok'|'warn', msg}

  useEffect(() => {
    let live = true;
    const ctl = new AbortController();
    setData(null);
    setLinks(null);
    riskApi.risk(setupId, itemId, { links: 'false' })
      .then((d) => {
        if (!live) return;
        setData(d);
        if (!d.links_pending) return;
        setLinks({ done: 0, total: d.links_pending });
        riskApi.riskLinks(setupId, itemId, (m) => {
          if (!live) return;
          if (m.type === 'progress') setLinks({ done: m.done, total: m.total });
          if (m.type === 'done') {
            setData((prev) => prev && { ...prev, risk: withUrls(prev.risk, m.urls || {}) });
            setLinks(null);
          }
        }, ctl.signal).catch(() => { if (live) setLinks(null); });
      })
      .catch((e) => { if (live) setError(e.message); });
    riskApi.history(setupId, itemId)
      .then((d) => { if (live) setHist(d.history || []); })
      .catch(() => { if (live) setHist([]); });
    return () => { live = false; ctl.abort(); };
  }, [setupId, itemId]);

  const riskLoaded = !!data;
  useEffect(() => {
    if (!riskLoaded) return undefined;
    let live = true;
    riskApi.mitigationOptions(setupId, itemId)
      .then((o) => { if (live) { setMit(o); setMitType(o.default_code || ''); } })
      .catch(() => { if (live) setMit({ can_add: false, types: [] }); });
    return () => { live = false; };
  }, [riskLoaded, setupId, itemId]);

  // Re-read the risk without blanking the page — the panel was invalidated by the add, so this
  // rebuilds it and the new mitigation appears from Orcanos itself.
  const reloadQuietly = useCallback(() => {
    riskApi.risk(setupId, itemId, { links: 'true' }).then(setData).catch(() => {});
  }, [setupId, itemId]);

  const mitApi = useMemo(() => {
    const wrap = (p) => p.then((d) => ({ ok: true, data: d })).catch((e) => ({ ok: false, data: { detail: e.message, unfit: e.detail?.unfit } }));
    return {
      key: `${setupId}|${itemId}|${mitType}`,
      checkAi: () => Promise.resolve({ available: true }),
      generate: () => wrap(riskApi.draftMitigations(setupId, itemId, mitType)),
      add: ({ name, description }) => wrap(riskApi.addMitigation(setupId, itemId, { type_code: mitType, name, description })),
      dupCheck: (drafts) => wrap(riskApi.dupCheckMitigations(setupId, itemId, mitType, drafts)),
      // The AI said this type cannot control the risk and named ones that can — switching
      // the type changes `key`, so the modal regenerates for the new type.
      pickType: (code) => setMitType(String(code)),
    };
  }, [setupId, itemId, mitType]);

  const mitLabel = (mit?.types || []).find((t) => t.code === mitType)?.label || 'Mitigation';

  const onAdded = (res) => {
    setAddedAny(true);
    const it = res.item || {};
    // Show it in the chain at once; the quiet reload replaces this with Orcanos's own view.
    setData((prev) => prev && {
      ...prev,
      risk: {
        ...prev.risk,
        chains: [...(prev.risk.chains || []),
          { key: it.display_key || it.key, name: it.name, url: it.url, link: it.link || null, tests: [], tests_known: false }],
      },
    });
    if (res.status === 'trace_failed') setNotice({ kind: 'warn', msg: res.error });
    else if (res.warning) setNotice({ kind: 'warn', msg: res.warning });
  };

  const closeAdd = () => {
    setAdding(false);
    if (addedAny) { setAddedAny(false); reloadQuietly(); }
  };

  // Stable identity: ItemDetails re-runs its load whenever this changes, and App re-renders
  // every second (Critical Note #59).
  const loadForm = useCallback(() => riskApi.riskForm(setupId, itemId), [setupId, itemId]);

  const r = data?.risk;
  return (
    <div className="drv-page rsk-page">
      <div className="rsk-crumbs">
        <Link to="/risk">Risk</Link><span>/</span>
        <span className="rsk-dim">{data?.panel_name || 'Risk file'}</span><span>/</span>
        <strong>{r?.key || itemId}</strong>
      </div>

      {error && <div className="drv-error" role="alert">{error}</div>}
      {!data && !error && (
        <div className="rsk-progress" role="status">
          <div className="rsk-progress-label">Loading the risk…</div>
          <div className="rsk-progress-bar rsk-progress-bar--busy"><i /></div>
        </div>
      )}

      {r && (
        <>
          <div className="drv-head">
            <div style={{ minWidth: 0 }}>
              <div className="drv-eyebrow">{r.key}{r.status ? ` · ${r.status}` : ''}{r.fmea_type ? ` · ${r.fmea_type}` : ''}</div>
              <h1 className="drv-title" style={{ marginTop: 4 }}>{r.name || '(no name)'}</h1>
            </div>
            <div className="rsk-actions">
              <ControlPill control={r.control} />
              {r.url && <a className="drv-btn" href={r.url} target="_blank" rel="noopener noreferrer">Open in Orcanos ↗</a>}
              <Link className="drv-btn" to={`/risk/${setupId}/studio`}>Ask about this risk</Link>
            </div>
          </div>

          {r.anomalies?.length > 0 && (
            <div className="drv-note">
              <strong>Worth a look</strong>
              <ul className="rsk-tip-list">
                {anomalyItems(r.anomalies).map((a, i) => <li key={i}><strong>{a.title}</strong>{a.body && <span>{a.body}</span>}</li>)}
              </ul>
            </div>
          )}

          <section className="rsk-card rsk-pad" aria-label="Trace chain">
            <div className="rsk-lblrow">
              <div className="rsk-lbl">Is it controlled? — risk → mitigation → verification → last run</div>
              {mit?.can_add && mitType && (
                <button type="button" className="drv-btn rsk-addmit" onClick={() => { setNotice(null); setAdding(true); }}>
                  ＋ Add mitigation
                </button>
              )}
            </div>
            {notice && (
              <div className={notice.kind === 'warn' ? 'drv-note' : 'rsk-fine'} role="status" style={{ marginTop: 8 }}>
                {notice.msg}
              </div>
            )}
            {links && (
              <div className="rsk-progress" role="status">
                <div className="rsk-progress-label">
                  Getting Orcanos links for the related items… {links.done} of {links.total}
                </div>
                <div className="rsk-progress-bar">
                  <i style={{ width: `${links.total ? Math.round((100 * links.done) / links.total) : 0}%` }} />
                </div>
              </div>
            )}
            {!data.verification && (
              <div className="rsk-fine">
                This panel has no test case filter, so test results cannot be read — tests found through the
                relations are listed without one. Edit the panel to add a filter with Last Test Run Result.
              </div>
            )}
            {(r.chains || []).length === 0 && (
              <div className="rsk-dim" style={{ marginTop: 8 }}>
                No mitigation is traced to this risk.{r.needs_control ? ' Its level needs one.' : ' Its level does not need one.'}
              </div>
            )}
            {(r.chains || []).map((c) => (
              <div className="rsk-chain" key={c.key}>
                <div className="rsk-node rsk-node--risk"><small>Risk</small><b><KeyLink href={r.url} k={r.key} /></b></div>
                <div className="rsk-arr" aria-hidden="true">→</div>
                <div className="rsk-node">
                  <small>Mitigation{c.link ? ` · ${c.link}` : ''}</small><b><KeyLink href={c.url} k={c.key} /></b>{c.name && <span>{c.name}</span>}
                </div>
                {(data.verification || c.tests_known) && (
                  <>
                    <div className="rsk-arr" aria-hidden="true">→</div>
                    <div className="rsk-node rsk-node--tests">
                      <small>Verification · {c.tests.length} test{c.tests.length === 1 ? '' : 's'}</small>
                      {c.tests.length === 0 && <span className="rsk-miss">No test traces to this requirement</span>}
                      {c.tests.map((t) => {
                        const st = RESULT_STYLE[t.result] || RESULT_STYLE.not_run;
                        return (
                          <div className="rsk-test" key={t.key} style={{ background: st.background, borderColor: st.borderColor }}>
                            <b><KeyLink href={t.url} k={t.key} /></b><span>{t.name}</span><em>{t.result_text || st.label}{t.link ? ` · ${t.link}` : ''}</em>
                            {(t.runs || []).length > 0 && (
                              <div className="rsk-runs" aria-label={`Test runs of ${t.key}`}>
                                {t.runs.map((run, k) => (
                                  <span key={k} className={`rsk-run--${run.result}`}>
                                    Run · <b>{run.exec || 'execution'}</b> · {run.result_text || RESULT_STYLE[run.result]?.label || run.result}
                                    {run.defect ? ` · defect ${run.defect}` : ''}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            ))}
            {(r.excluded_links || []).length > 0 && (
              <div className="rsk-fine" style={{ marginTop: 10 }}>
                Not counted as mitigations — traced with a link type that is not a mitigation or control:{' '}
                {r.excluded_links.map((e) => `${e.key} (${e.link})`).join(', ')}
              </div>
            )}
            {(r.complaints || []).length > 0 && (
              <div className="rsk-fine" style={{ marginTop: 10 }}>
                Field signal: {r.complaints.length} complaint{r.complaints.length === 1 ? '' : 's'} traced — {r.complaints.join(', ')}
              </div>
            )}
          </section>

          <div className="rsk-two">
            <section className="rsk-card" style={{ flex: '2 1 520px', minWidth: 0 }}>
              <div className="rsk-tabs" role="tablist">
                {[['trace', 'Hazard analysis'], ['props', 'All properties (Orcanos)']].map(([k, label]) => (
                  <button key={k} type="button" role="tab" aria-selected={tab === k}
                    className={tab === k ? 'on' : ''} onClick={() => setTab(k)}>{label}</button>
                ))}
              </div>
              {tab === 'trace' ? (
                <div className="rsk-pad rsk-facts">
                  {[['Hazard', r.hazard], ['Hazardous situation', r.situation], ['Failure mode', r.failure_mode],
                    ['Cause', r.cause], ['Harm', r.harm], ['Component', r.component],
                    ['Risk control', r.control_text], ['Control type', r.control_type]]
                    .filter(([, v]) => v)
                    .map(([k, v]) => (<div className="rsk-fact" key={k}><span>{k}</span><span>{v}</span></div>))}
                  {![r.hazard, r.situation, r.failure_mode, r.cause, r.harm, r.control_text].some(Boolean) && (
                    <div className="rsk-dim">The filter does not select the hazard-analysis columns — see All properties.</div>
                  )}
                </div>
              ) : (
                <div className="rsk-pad"><ItemDetails load={loadForm} /></div>
              )}
            </section>

            <section className="rsk-card rsk-pad" style={{ flex: '1 1 320px' }} aria-label="Scores">
              <div className="rsk-lbl">Scores — {settingsName(data.settings)}</div>
              <div className="rsk-scores">
                <Score label="Severity" v={r.s_pre} />
                <Score label="P before" v={r.p_pre} />
                <Score label="P after" v={r.p_post} />
                {(r.d_pre || r.d_post) ? <Score label="D before / after" v={`${r.d_pre || '–'} / ${r.d_post || '–'}`} /> : null}
              </div>
              <div className="rsk-levelrow"><span>Before</span><LevelPill level={r.level_pre} /></div>
              <div className="rsk-levelrow"><span>After</span><LevelPill level={r.level_post} /></div>
              {(r.level_pre?.source === 'computed' || r.level_post?.source === 'computed') && (
                <div className="rsk-fine">* computed from P and S — the filter has no stored level.</div>
              )}

              <div className="rsk-lbl" style={{ marginTop: 16 }}>Across baselines</div>
              {hist && hist.length <= 1 && <div className="rsk-fine">No baselines yet — take one from the risk file.</div>}
              {hist && hist.length > 1 && hist.map((h) => (
                <div className="rsk-hist" key={h.baseline_id || 'now'}>
                  <div><strong>{h.name}</strong>{h.when && <span className="rsk-fine"> · {String(h.when).slice(0, 10)}</span>}</div>
                  {h.present
                    ? <div className="rsk-hist-r"><LevelPill level={h.level_post} /><span className="rsk-fine">{CONTROL_NAMES[h.control] || ''}</span></div>
                    : <span className="rsk-fine">not in this baseline</span>}
                </div>
              ))}
              <Link className="drv-btn" style={{ marginTop: 10 }} to={`/risk/${setupId}/baselines`}>Compare baselines</Link>
            </section>
          </div>
        </>
      )}

      {adding && r && (
        <AddItemModal
          setupId={setupId}
          ctx={{
            childType: mitLabel,
            parentKey: mit.risk_key || r.key,
            subtitle: "Created in the risk file's project & version, linked as a mitigation",
            linkType: mit.link_type || '',
            dupHint: 'Check these drafts against the mitigations already in this risk file, and each other',
          }}
          canAskPaul={!!mit.can_ai}
          api={mitApi}
          headerExtra={(mit.types || []).length > 1 && (
            <div className="rsk-mittype">
              <span>Type</span>
              <SearchSelect ariaLabel="Mitigation type" placeholder="Search types…" value={mitType}
                onChange={(v) => setMitType(String(v))}
                options={mit.types.filter((t) => t.can_add).map((t) => ({ value: t.code, label: t.label }))} />
            </div>
          )}
          onClose={closeAdd}
          onDone={(res) => { onAdded(res); setAdding(false); reloadQuietly(); if (res.status === 'linked' && !res.warning) setNotice({ kind: 'ok', msg: `${res.item?.display_key || 'The mitigation'} was added and linked to ${r.key}.` }); }}
          onAdded={onAdded}
        />
      )}
    </div>
  );
}

/** A copy of the risk with the streamed links filled in on every mitigation and test. */
function withUrls(risk, urls) {
  // Same rule as risk_engine.item_id_from_key: `TC-21071 (11630)` → 21071, not the original id.
  const id = (key) => String(key || '').trim().match(/^[A-Za-z][A-Za-z0-9_]*-(\d+)/)?.[1] || '';
  return {
    ...risk,
    chains: (risk.chains || []).map((c) => ({
      ...c,
      url: c.url || urls[id(c.key)] || '',
      tests: (c.tests || []).map((t) => ({ ...t, url: t.url || urls[id(t.key)] || '' })),
    })),
  };
}

const CONTROL_NAMES = {
  uncontrolled: 'Uncontrolled', failed: 'Failed', unverified: 'Unverified', not_run: 'Not run',
  mitigated: 'Mitigated', controlled: 'Controlled', accepted: 'Accepted', unscored: 'No level',
};

function Score({ label, v }) {
  return (
    <div className="rsk-sc"><small>{label}</small><b>{v || '–'}</b></div>
  );
}

function settingsName(s) {
  if (!s) return '';
  if (s.source === 'default') return 'default 5 × 5 table';
  return s.method === 'scalar' ? 'Orcanos FMEA settings (scalar RPN)' : 'Orcanos FMEA settings (P × S)';
}
